// src/components/practice/QuizQuestion.jsx
//
// SHARED per-question body for the objective quiz types (mcq / fill / matching),
// used by BOTH the set-text QuizView and the literature PracticeQuiz so the render
// + reveal live in one place (grading lives in practiceHelpers -- isQuestionCorrect).
// Faithful to the prototype LitQuizMCQ / LitQuizFill / LitQuizMatch (book-* theme).
//
// Renders the ANSWER widget + the post-submit reveal only; the parent renders the
// question text/label and owns the Check/Submit button (gated via isAnswerComplete).
//   props: { q, answer, setAnswer, submitted }
//   answer shape: mcq -> option index | fill -> string | matching -> { leftIdx: rightIdx }
import React, { useMemo, useState } from 'react';
import { isQuestionCorrect, shuffledMatchOptions } from './practiceHelpers.js';
import { optionState, quizFeedback } from './feedbackState.js';
import '../reader/lesson.css';

export default function QuizQuestion({ q, answer, setAnswer, submitted, collapsedHints = false }) {
    if (!q) return null;
    const props = { q, answer, setAnswer, submitted, collapsedHints };
    if (q.type === 'fill') return <FillQuestion {...props} />;
    if (q.type === 'matching') return <MatchingQuestion {...props} />;
    return <McqQuestion {...props} />;
}

// Opt-in for a guided lesson: keep the pooled runners' existing presentation.
function QuestionHint({ hint, collapsed = false }) {
    const [open, setOpen] = useState(false);
    if (!hint) return null;
    if (!collapsed) return <p className="font-serif italic text-book-muted text-xs mb-3">Hint: {hint}</p>;
    return <div className="btl-activity-disclosure mb-4"><button type="button" aria-expanded={open} onClick={() => setOpen(!open)}>Optional hint</button>
        {open && <p className="font-serif italic text-book-muted text-sm">{hint}</p>}</div>;
}

function Explanation({ q }) {
    if (!q.explanation) return null;
    return (
        <div className="mt-3 rounded-[var(--radius-card)] border border-book-line bg-book-page p-3">
            <div className="font-display text-book-accent text-xs mb-1.5 font-semibold">Explanation</div>
            <p className="font-serif italic text-book-text leading-relaxed text-sm whitespace-pre-wrap break-words">{q.explanation}</p>
        </div>
    );
}

// lesson-3b: ONE feedback surface for all three question types, driven by the pure
// quizFeedback() mapping. Correct is a --success tint with a tick; wrong is NEUTRAL
// (not red) with "Not quite" -- a drill is for learning, and a wall of red on a first
// attempt teaches nothing the explanation below does not teach better.
export function AnswerFeedback({ q, answer }) {
    const f = quizFeedback(q, answer);
    // A hint is worth surfacing on a wrong answer -- before submitting it is a nudge,
    // after it is the thing that explains the miss. Only when there is no explanation,
    // which says it better.
    const hint = (!f.correct && !q.explanation && typeof q.hint === 'string' && q.hint.trim()) ? q.hint.trim() : '';
    return (
        <div className={'mt-4 rounded-[var(--radius-card)] border p-3 '
            + (f.correct ? 'border-book-success/50 bg-book-success/10' : 'border-book-line bg-book-bg')}>
            <div className="flex items-center gap-2">
                {f.correct ? (
                    <span className="w-4 h-4 rounded-full bg-book-success text-book-pageDark flex items-center justify-center flex-shrink-0">
                        <svg className="w-2.5 h-2.5" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                            strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <polyline points="20 6 9 17 4 12" />
                        </svg>
                    </span>
                ) : null}
                {/* The WORD stays in normal ink. --success green on its own 10% green
                    tint measured 3.05:1 in Light and 3.26:1 in Minimalist -- below AA
                    for small bold text. The green carries in the filled tick badge and
                    the card's border instead, which are UI shapes, not text, and the
                    tick is a colour-independent signal in its own right. */}
                <span className="font-display text-xs font-semibold text-book-text">
                    {f.title}
                </span>
                {f.detail && <span className="font-serif text-book-muted text-xs">{f.detail}</span>}
            </div>
            {hint && <p className="font-serif italic text-book-muted text-xs mt-2">{hint}</p>}
        </div>
    );
}

function McqQuestion({ q, answer, setAnswer, submitted, collapsedHints }) {
    const sel = answer == null ? null : Number(answer);
    // lesson-3b: option rows are cards, and their state comes from the pure
    // optionState() mapping rather than three inline conditionals.
    const CLS = {
        idle: 'border-book-line bg-book-page hover:border-book-accent/60',
        selected: 'border-book-accent bg-book-accent/10',
        correct: 'border-book-success/60 bg-book-success/10',
        wrong: 'border-book-muted/50 bg-book-bg',
        muted: 'border-book-line bg-book-page opacity-60',
    };
    return (
        <div>
            {q.hint && !submitted && (collapsedHints ? <QuestionHint hint={q.hint} collapsed /> : (
                <div className="text-xs font-serif italic text-book-muted border-l-2 border-book-accent/30 pl-3 mb-4">
                    <span className="text-book-accent not-italic font-display text-xs mr-2 font-semibold">Hint:</span>{q.hint}
                </div>
            ))}
            <div className="space-y-2.5">
                {q.options.map((opt, oi) => {
                    const state = optionState({ submitted, optionIndex: oi, correctIndex: q.correct, selectedIndex: sel });
                    return (
                        <button key={oi} onClick={() => !submitted && setAnswer(oi)} disabled={submitted}
                            aria-pressed={sel === oi}
                            className={`w-full text-left p-3.5 rounded-[var(--radius-card)] border font-serif flex items-start gap-3 transition-colors ${CLS[state]}`}>
                            <span className="font-display text-sm font-bold flex-shrink-0 mt-0.5 text-book-accent">{String.fromCharCode(65 + oi)}</span>
                            <span className="text-sm text-book-text flex-grow">{opt}</span>
                            {state === 'correct' && (
                                <svg className="w-4 h-4 flex-shrink-0 mt-0.5 text-book-success" viewBox="0 0 24 24" fill="none"
                                    stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                    <polyline points="20 6 9 17 4 12" />
                                </svg>
                            )}
                        </button>
                    );
                })}
            </div>
            {submitted && <AnswerFeedback q={q} answer={answer} />}
            {submitted && <Explanation q={q} />}
        </div>
    );
}

function FillQuestion({ q, answer, setAnswer, submitted, collapsedHints }) {
    const val = typeof answer === 'string' ? answer : '';
    const correct = submitted && isQuestionCorrect(q, val);
    return (
        <div>
            {q.hint && !submitted && <QuestionHint hint={q.hint} collapsed={collapsedHints} />}
            <input type="text" value={val} onChange={(e) => !submitted && setAnswer(e.target.value)} disabled={submitted}
                aria-label={q.question || 'Your answer'}
                placeholder="Type your answer"
                className={`selectable w-full md:w-96 px-3 py-2 bg-book-bg border-b-2 font-serif outline-none ${submitted ? (correct ? 'border-book-success text-book-text' : 'border-book-muted text-book-text') : 'border-book-accent/60 text-book-text focus:border-book-accent'}`} />
            {/* 3b: one shared feedback surface (see AnswerFeedback) replaces the
                green/red boxes -- wrong is neutral, not an alarm. */}
            {submitted && <AnswerFeedback q={q} answer={answer} />}
            {submitted && <Explanation q={q} />}
        </div>
    );
}

function MatchingQuestion({ q, answer, setAnswer, submitted, collapsedHints }) {
    const pairs = q.pairs || [];
    // Parent readers may reconstruct mapped objects after an answer changes.
    // Key by actual question/pair content so that selection never reshuffles it.
    const shuffleKey = JSON.stringify([q.id || '', pairs]);
    const shuffledRights = useMemo(() => shuffledMatchOptions(JSON.parse(shuffleKey)[1]), [shuffleKey]);
    const mapping = (answer && typeof answer === 'object') ? answer : {};
    const [activeLeft, setActiveLeft] = useState(null);
    const usedRights = new Set(Object.values(mapping));

    const pickLeft = (i) => { if (!submitted) setActiveLeft(i); };
    const pickRight = (originalIdx) => {
        if (submitted || activeLeft == null) return;
        const next = { ...mapping };
        Object.keys(next).forEach((k) => { if (next[k] === originalIdx) delete next[k]; });
        next[activeLeft] = originalIdx;
        setAnswer(next);
        setActiveLeft(null);
    };
    const clear = (leftIdx) => { if (submitted) return; const next = { ...mapping }; delete next[leftIdx]; setAnswer(next); };

    return (
        <div>
            {collapsedHints && q.hint && !submitted && <QuestionHint hint={q.hint} collapsed />}
            <p className="font-serif italic text-book-muted text-xs mb-3">Choose a left-hand item, then the right-hand item it pairs with. {collapsedHints ? 'Use Clear pair to change a match.' : 'Click the cross to clear a pair.'}</p>
            <div className="grid md:grid-cols-2 gap-3 mb-3">
                <div className="space-y-2">
                    <div className="font-display text-book-accent text-xs mb-2 font-semibold">Items</div>
                    {pairs.map((p, i) => {
                        const paired = mapping[i] !== undefined;
                        const correctMatch = submitted && mapping[i] === i;
                        const wrongMatch = submitted && paired && mapping[i] !== i;
                        const isActive = activeLeft === i;
                        const cls = submitted
                            ? (correctMatch ? 'border-book-success/60 bg-book-success/10 text-book-text' : (wrongMatch ? 'border-book-muted/50 bg-book-bg text-book-text' : 'border-book-line text-book-text'))
                            : (isActive ? 'border-book-accent bg-book-accent/15 text-book-accent' : (paired ? 'border-book-accent/60 bg-book-page/40 text-book-text' : 'border-book-accent/30 hover:border-book-accent/60 text-book-text'));
                        if (collapsedHints) return <div key={i} className={`p-3 font-serif border ${cls}`}>
                            <button type="button" aria-pressed={isActive} onClick={() => pickLeft(i)} disabled={submitted} className="w-full text-left text-sm leading-snug">{p.left}</button>
                            {paired && <div className="font-display text-book-accent text-xs mt-1 font-semibold">-&gt; {pairs[mapping[i]]?.right}</div>}
                            {paired && !submitted && <button type="button" className="mt-2 text-xs underline" onClick={() => clear(i)} aria-label={`Clear pair ${i + 1}`}>Clear pair</button>}
                        </div>;
                        return (
                            <button key={i} onClick={() => pickLeft(i)} disabled={submitted} className={`w-full text-left p-3 font-serif border transition-colors ${cls}`}>
                                <div className="flex items-start justify-between gap-2">
                                    <span className="text-sm leading-snug">{p.left}</span>
                                    {paired && !submitted && (
                                        <span onClick={(e) => { e.stopPropagation(); clear(i); }} className="text-book-muted hover:text-book-accent text-xs cursor-pointer">&times;</span>
                                    )}
                                </div>
                                {paired && <div className="font-display text-book-accent text-xs mt-1 font-semibold">-&gt; {pairs[mapping[i]] && pairs[mapping[i]].right}</div>}
                            </button>
                        );
                    })}
                </div>
                <div className="space-y-2">
                    <div className="font-display text-book-accent text-xs mb-2 font-semibold">Match to</div>
                    {shuffledRights.map(({ text, originalIdx }) => {
                        const used = usedRights.has(originalIdx);
                        return (
                            <button key={originalIdx} onClick={() => pickRight(originalIdx)} disabled={submitted || used || activeLeft == null}
                                className={`w-full text-left p-3 font-serif border transition-colors text-sm leading-snug ${used ? 'border-book-accent/15 text-book-muted opacity-50 cursor-not-allowed' : (activeLeft != null ? 'border-book-accent/60 hover:border-book-accent bg-book-page/40 text-book-text cursor-pointer' : 'border-book-accent/30 text-book-text')}`}>
                                {text}
                            </button>
                        );
                    })}
                </div>
            </div>
            {submitted && <AnswerFeedback q={q} answer={answer} />}
            {submitted && <Explanation q={q} />}
        </div>
    );
}
